import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';
import { Schema, Predicate } from 'effect';
import { voiceLangToLangLabel } from '@/lib/utils';

export { SUPPORTED_TEXT_TO_AUDIO_LANGUAGES } from '@listening/frontend/tts/language-support';

export const MAX_TEXT_LENGTH = 10_000_000;
export const VERY_SHORT_TEXT_LENGTH = 5;
export const AVG_WORDS_PER_MINUTE = 150;

// `Schema.Trim` decodes to the trimmed string, so the length checks below see
// the same value the form submits — matching zod's `.trim().min().max()` order.
const textToAudioTextSchema = Schema.Trim.check(
  Schema.isMinLength(1, { message: m.try_magic_error_no_text() }),
  Schema.isMaxLength(MAX_TEXT_LENGTH, {
    message: m.dialog_text_to_audio_too_long({
      max: MAX_TEXT_LENGTH.toString(),
    }),
  }),
);

const textToAudioBaseSchema = Schema.Struct({
  text: textToAudioTextSchema,
});

const textToAudioSchemaWithTitle = Schema.Struct({
  ...textToAudioBaseSchema.fields,
  title: Schema.optional(Schema.Trim.check(Schema.isMaxLength(100))),
});

export function makeTextToAudioSchema(options?: {
  includeTitle?: false;
}): typeof textToAudioBaseSchema;
export function makeTextToAudioSchema(options: {
  includeTitle: true;
}): typeof textToAudioSchemaWithTitle;
export function makeTextToAudioSchema({
  includeTitle = false,
}: {
  includeTitle?: boolean;
} = {}) {
  return includeTitle ? textToAudioSchemaWithTitle : textToAudioBaseSchema;
}

export function deriveTextUploadFilename({
  title,
  text,
}: {
  title?: string | undefined;
  text: string;
}) {
  const base = deriveTextToAudioTitle({
    title,
    text,
  })
    .replaceAll(/\s+/g, ' ')
    .slice(0, 60)
    .replaceAll(/[\\/:*?"<>|]/g, '')
    .trim();

  return `${base || m.dialog_text_to_audio_default_filename()}.txt`;
}

export function deriveTextToAudioTitle({
  title,
  text,
}: {
  title?: string | undefined;
  text: string;
}) {
  return (
    title?.trim() ||
    text.trim().replaceAll(/\s+/g, ' ').slice(0, 60).trim() ||
    m.dialog_text_to_audio_title_placeholder()
  );
}

export function countWords(text: string) {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function formatListeningTime(wordCount: number) {
  if (wordCount === 0) return null;

  const totalMinutes = Math.ceil(wordCount / AVG_WORDS_PER_MINUTE);
  if (totalMinutes < 60) {
    return m.dialog_text_to_audio_listening_time_minutes({
      count: totalMinutes.toString(),
    });
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return m.dialog_text_to_audio_listening_time_hours({
      count: hours.toString(),
    });
  }

  return m.dialog_text_to_audio_listening_time_hours_minutes({
    hours: hours.toString(),
    minutes: minutes.toString(),
  });
}

export function getTextToAudioLanguageName(lang: string, locale = getLocale()) {
  const displayNames = Predicate.isFunction(Intl.DisplayNames)
    ? new Intl.DisplayNames([locale], { type: 'language' })
    : null;

  return (
    displayNames?.of(lang) ??
    voiceLangToLangLabel[lang as keyof typeof voiceLangToLangLabel] ??
    lang
  );
}
